// 用户模块服务层

import utils, { AdUtils } from "../../../utils";
import { userApi } from "../api";
import { User, LoginRequest, RegisterRequest } from "../types";
import { LoginResponse, RegisterResponse } from "../types/auth.types";

// 用户服务类
export class UserService {
  private static instance: UserService;
  // 请求去重：存储当前正在进行的请求 Promise
  private pendingUserRequest: Promise<User> | null = null;
  // 最后一次成功加载的时间戳
  private lastUserLoadTime = 0;
  // 最小加载间隔（毫秒）
  private static readonly MIN_USER_LOAD_INTERVAL = 2000;

  static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  // 用户认证服务
  async login(data: LoginRequest): Promise<LoginResponse> {
    try {
      const result = await userApi.login(data);
      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "login failed";
      throw new Error(errorMessage);
    }
  }

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    try {
      const result = await userApi.register(data);
      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "register failed";
      throw new Error(errorMessage);
    }
  }

  logout(): void {
    utils.setUser(null);
    utils._clearObject();
    utils.deleteAllCookies();
    utils.setJustLoggedOut();
    window.location.replace("/");
  }

  // 用户数据服务（带节流和去重）
  async loadUserInfo(): Promise<User> {
    const now = Date.now();

    // 节流：如果距离上次加载时间小于最小间隔，且有正在进行的请求，直接返回该请求
    if (
      now - this.lastUserLoadTime < UserService.MIN_USER_LOAD_INTERVAL &&
      this.pendingUserRequest
    ) {
      return this.pendingUserRequest;
    }

    // 去重：如果已有正在进行的请求，直接返回该请求
    if (this.pendingUserRequest) {
      return this.pendingUserRequest;
    }

    // 创建新请求
    this.pendingUserRequest = this._doLoadUserInfo();

    try {
      const result = await this.pendingUserRequest;
      AdUtils.checkPurchase();
      this.lastUserLoadTime = Date.now();
      return result;
    } finally {
      this.pendingUserRequest = null;
    }
  }

  // 实际执行加载用户信息的内部方法
  private async _doLoadUserInfo(): Promise<User> {
    try {
      const data = await userApi.getUserInfo();
      return data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "load user info failed";
      throw new Error(errorMessage);
    }
  }

  // 游客数据服务
  async loadGuestInfo(): Promise<User> {
    try {
      const data = await userApi.getGuestInfo();
      return data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "load guest info failed";
      throw new Error(errorMessage);
    }
  }

  // 更新用户余额
  async updateUserBalance(): Promise<{ balance: number; currency: string }> {
    try {
      const balanceData = await userApi.getUserBalance();
      return balanceData;
    } catch (error) {
      console.error("update user balance failed:", error);
      throw error;
    }
  }

  // 更新用户信息
  async updateUserInfo(data: Partial<User>): Promise<User> {
    try {
      const updatedUser = await userApi.updateUserInfo(data);
      return updatedUser;
    } catch (error) {
      console.error("update user info failed:", error);
      throw error;
    }
  }
}

// 创建单例实例
export const userService = UserService.getInstance();

// 导出便捷方法
export const {
  login,
  register,
  logout,
  loadUserInfo,
  loadGuestInfo,
  updateUserBalance,
  updateUserInfo,
} = userService;
